import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatIDR, formatApiErrorDetail, formatDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, CalendarBlank, Minus, Plus, ShoppingCart } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ev, setEv] = useState(null);
  const [qty, setQty] = useState({});
  const [buyer, setBuyer] = useState({ buyer_name: "", buyer_email: "", buyer_phone: "" });
  const [paymentMethod, setPaymentMethod] = useState("midtrans");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    api.get(`/events/${id}`).then((r) => {
      setEv(r.data);
      setQty(Object.fromEntries(r.data.ticket_types.map((t) => [t.id, 0])));
    }).catch(() => toast.error("Acara tidak ditemukan"));
  }, [id]);

  useEffect(() => {
    if (user) setBuyer((b) => ({ ...b, buyer_name: user.name || "", buyer_email: user.email || "", buyer_phone: user.phone || "" }));
  }, [user]);

  if (!ev) return <div className="min-h-screen bg-background"><Navbar /><div className="p-20 text-center text-zinc-500">Memuat...</div></div>;

  const setQ = (ttId, val, max) => setQty({ ...qty, [ttId]: Math.max(0, Math.min(max, val)) });
  const total = ev.ticket_types.reduce((s, t) => s + t.price * (qty[t.id] || 0), 0);
  const count = Object.values(qty).reduce((a, b) => a + b, 0);

  const checkout = async () => {
    if (!user) { navigate("/login", { state: { from: `/events/${id}` } }); return; }
    if (count === 0) { toast.error("Pilih minimal 1 tiket"); return; }
    if (!buyer.buyer_name || !buyer.buyer_email || !buyer.buyer_phone) { toast.error("Lengkapi data pemesan"); return; }
    setProcessing(true);
    try {
      const items = Object.entries(qty).filter(([, q]) => q > 0).map(([ticket_type_id, quantity]) => ({ ticket_type_id, quantity }));
      const { data } = await api.post("/orders/checkout", {
        event_id: id, ...buyer, items, payment_method: paymentMethod, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal memproses");
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <div className="relative h-72 md:h-96 overflow-hidden border-b border-white/10">
        <img src={ev.poster_url} alt={ev.name} onError={(e) => { e.currentTarget.src = "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1600&q=80"; }} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
        <div className="absolute bottom-0 left-0 right-0 max-w-7xl mx-auto px-5 sm:px-8 pb-8">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] bg-primary text-black px-2.5 py-1">{ev.category}</span>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter mt-3 leading-none">{ev.name}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10 grid lg:grid-cols-3 gap-10">
        {/* left: info + tickets */}
        <div className="lg:col-span-2 space-y-8">
          <div className="flex flex-wrap gap-6 text-zinc-300">
            <span className="flex items-center gap-2"><CalendarBlank size={20} className="text-primary" /> {formatDateTime(ev.date)}{ev.end_date ? ` — ${formatDateTime(ev.end_date)}` : ""}</span>
            <span className="flex items-center gap-2"><MapPin size={20} className="text-primary" /> {ev.location}</span>
          </div>
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Deskripsi</h2>
            <p className="text-zinc-400 leading-relaxed">{ev.description}</p>
          </div>
          <div>
            <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-4">Pilih Tiket</h2>
            <div className="space-y-3">
              {ev.ticket_types.map((t) => {
                const soldOut = t.remaining <= 0;
                return (
                  <div key={t.id} data-testid={`ticket-type-${t.id}`}
                    className={`flex items-center justify-between border p-4 transition-all ${qty[t.id] > 0 ? "border-primary bg-primary/5" : "border-white/10"}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-heading font-bold text-lg text-white">{t.name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500">sisa {t.remaining}</span>
                      </div>
                      <p className="text-primary font-semibold">{formatIDR(t.price)}</p>
                    </div>
                    {soldOut ? (
                      <span className="text-secondary text-sm font-bold uppercase">Habis</span>
                    ) : (
                      <div className="flex items-center gap-3">
                        <button data-testid={`qty-minus-${t.id}`} onClick={() => setQ(t.id, (qty[t.id] || 0) - 1, t.remaining)}
                          className="w-8 h-8 border border-white/20 flex items-center justify-center hover:border-primary hover:text-primary transition-colors">
                          <Minus size={14} weight="bold" />
                        </button>
                        <span data-testid={`qty-value-${t.id}`} className="w-6 text-center font-bold">{qty[t.id] || 0}</span>
                        <button data-testid={`qty-plus-${t.id}`} onClick={() => setQ(t.id, (qty[t.id] || 0) + 1, t.remaining)}
                          className="w-8 h-8 border border-white/20 flex items-center justify-center hover:border-primary hover:text-primary transition-colors">
                          <Plus size={14} weight="bold" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* right: checkout */}
        <div className="lg:col-span-1">
          <motion.div className="bg-card border border-white/10 p-6 lg:sticky lg:top-24">
            <h2 className="font-heading text-xl font-bold mb-4">Data Pemesan</h2>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Nama</Label>
                <Input data-testid="buyer-name" value={buyer.buyer_name} onChange={(e) => setBuyer({ ...buyer, buyer_name: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-800 focus:ring-primary focus:border-primary rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
                <Input data-testid="buyer-email" type="email" value={buyer.buyer_email} onChange={(e) => setBuyer({ ...buyer, buyer_email: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-800 focus:ring-primary focus:border-primary rounded-none" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-zinc-400">No HP</Label>
                <Input data-testid="buyer-phone" value={buyer.buyer_phone} onChange={(e) => setBuyer({ ...buyer, buyer_phone: e.target.value })}
                  className="mt-1 bg-zinc-900 border-zinc-800 focus:ring-primary focus:border-primary rounded-none" />
              </div>
            </div>

            <div className="border-t border-white/10 mt-5 pt-5 space-y-2">
              {ev.ticket_types.filter((t) => qty[t.id] > 0).map((t) => (
                <div key={t.id} className="flex justify-between text-sm text-zinc-400">
                  <span>{t.name} × {qty[t.id]}</span>
                  <span>{formatIDR(t.price * qty[t.id])}</span>
                </div>
              ))}
              <div className="flex justify-between font-bold text-lg pt-2">
                <span>Total</span>
                <span data-testid="checkout-total" className="text-primary">{formatIDR(total)}</span>
              </div>
            </div>

            <div className="mt-5">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Metode Pembayaran</p>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="pay-method-midtrans" onClick={() => setPaymentMethod("midtrans")}
                  className={`text-left p-3 border transition-all ${paymentMethod === "midtrans" ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}>
                  <p className="font-heading font-bold text-sm text-white">Midtrans</p>
                  <p className="text-[11px] text-zinc-500 leading-tight mt-0.5">VA · QRIS · E-Wallet · Kartu</p>
                </button>
                <button type="button" data-testid="pay-method-stripe" onClick={() => setPaymentMethod("stripe")}
                  className={`text-left p-3 border transition-all ${paymentMethod === "stripe" ? "border-primary bg-primary/10" : "border-white/10 hover:border-white/30"}`}>
                  <p className="font-heading font-bold text-sm text-white">Stripe</p>
                  <p className="text-[11px] text-zinc-500 leading-tight mt-0.5">Kartu internasional</p>
                </button>
              </div>
            </div>

            <Button data-testid="checkout-btn" onClick={checkout} disabled={processing || count === 0}
              className="w-full mt-4 bg-primary text-black font-bold hover:bg-cyan-300 rounded-none h-12 gap-2">
              <ShoppingCart size={18} weight="bold" />
              {processing ? "Memproses..." : user ? "Bayar Sekarang" : "Masuk untuk Membeli"}
            </Button>
            <p className="text-[11px] text-zinc-600 mt-3 text-center">
              {paymentMethod === "stripe"
                ? "Pembayaran via Stripe. Kartu uji: 4242 4242 4242 4242"
                : "Anda akan diarahkan ke halaman pembayaran Midtrans (VA / QRIS / E-Wallet / Kartu)."}
            </p>
          </motion.div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
